/**
 * Curated sample lead payload used by /sample-report.
 *
 * Shape matches what Report.jsx expects from GET /leads/{id}:
 *   { lead_id, report, results, assessment, consultant, firmographics,
 *     consultant_perspective, whatsapp_link, mailto_link, booking_link,
 *     grounding }
 *
 * The persona is a UAE mid-market CFO of a manufacturing group — chosen
 * because the majority of BizPulseAI's early-adopter consultants sell into
 * this profile and it exercises every section of the report (grounded
 * citations, regulatory obligations, 30/90/180-day roadmap, benchmark
 * band, consultant perspective).
 */

const CITATIONS = [
  {
    token: "E1",
    kpi_code: "monthly_close_days",
    kpi_label: "Monthly close cycle",
    prospect_value: 12,
    p25: 6,
    p50: 7,
    p75: 9,
    unit: "days",
    band: "below_p25",
    severity: "high",
    confidence: 0.92,
    source_id: "wb_finance_ops_2024",
    source_label: "World Bank · Finance Ops 2024",
    source_url: "https://data.worldbank.org/",
    scope: "United Arab Emirates · Manufacturing",
  },
  {
    token: "E2",
    kpi_code: "forecast_horizon_qtrs",
    kpi_label: "Forecast horizon",
    prospect_value: 1,
    p25: 2,
    p50: 4,
    p75: 4,
    unit: "quarters",
    band: "below_p25",
    severity: "medium",
    confidence: 0.88,
    source_id: "kpmg_gcc_cfo_pulse_2025",
    source_label: "KPMG · GCC CFO Pulse 2025",
    source_url: "https://kpmg.com/",
    scope: "GCC · Mid-market · Finance",
  },
  {
    token: "E3",
    kpi_code: "sod_ap_coverage_pct",
    kpi_label: "AP segregation-of-duties coverage",
    prospect_value: 48,
    p25: 70,
    p50: 82,
    p75: 91,
    unit: "%",
    band: "below_p25",
    severity: "medium",
    confidence: 0.86,
    source_id: "isaca_controls_2024",
    source_label: "ISACA · Controls Benchmark 2024",
    source_url: "https://www.isaca.org/",
    scope: "Manufacturing · Global",
  },
  {
    token: "E4",
    kpi_code: "reporting_cadence_score",
    kpi_label: "Reporting cadence maturity",
    prospect_value: 79,
    p25: 55,
    p50: 65,
    p75: 74,
    unit: "score",
    band: "above_p75",
    severity: "low",
    confidence: 0.9,
    source_id: "deloitte_gcc_finance_2025",
    source_label: "Deloitte · GCC Finance Function Survey 2025",
    source_url: "https://www2.deloitte.com/",
    scope: "United Arab Emirates · Manufacturing",
  },
];

const REGULATORY_OBLIGATIONS = [
  {
    id: "R1",
    title: "ZATCA Phase-2 e-invoicing integration",
    body: "Manufacturing entities in the ZATCA reciprocal review scope must complete Phase-2 e-invoicing integration and validate B2B invoice clearance ahead of the 2027 review window. Non-compliance triggers financial penalties and reciprocal audit exposure.",
    jurisdiction: "SAU",
    priority: "P0",
    timeline: "By Q2 2027",
    publisher: "ZATCA (Zakat, Tax and Customs Authority)",
    url: "https://zatca.gov.sa/",
    supporting_kpis: ["e_invoice_integration_pct", "b2b_clearance_pass_rate"],
    tags: ["tax", "e-invoicing", "SAU"],
  },
  {
    id: "R2",
    title: "UAE Corporate Tax filing readiness",
    body: "Federal Tax Authority requires audited financial statements and consolidated group returns for taxable entities above AED 50M in revenue. Ensure close cadence supports statutory filing timeline.",
    jurisdiction: "UAE",
    priority: "P1",
    timeline: "Ongoing · annual filing",
    publisher: "UAE Federal Tax Authority",
    url: "https://tax.gov.ae/",
    supporting_kpis: ["monthly_close_days", "consolidation_lead_time"],
    tags: ["tax", "compliance", "UAE"],
  },
];

const RECOMMENDATION_CARDS = [
  {
    id: "REC-01",
    title: "Cut monthly close from 12 → 7 days",
    body: "Deploy intercompany-reconciliation automation across the three subsidiaries and shift AP invoice ingestion to weekly batches. Peer P50 at 7 days is proven achievable with off-the-shelf tooling.",
    priority: "P0",
    timeline: "Next 30 days",
    roi_band: "AED 4M+ working-capital release",
    confidence: 0.9,
    source_type: "playbook",
    evidence_refs: ["E1"],
    tags: ["finance-ops", "quick-win"],
  },
  {
    id: "REC-02",
    title: "Stand up a rolling 4-quarter forecast",
    body: "Move from single-quarter horizon to a rolling four-quarter cash + P&L forecast. Anchor scenarios on the top three revenue drivers surfaced during discovery.",
    priority: "P1",
    timeline: "Next 90 days",
    roi_band: "Board visibility · risk reduction",
    confidence: 0.85,
    source_type: "playbook",
    evidence_refs: ["E2"],
    tags: ["planning", "governance"],
  },
  {
    id: "REC-03",
    title: "Close AP segregation-of-duties gaps",
    body: "Introduce a two-person approval workflow for vendor invoices above AED 25k and route below-threshold invoices through a supervisor review sample. Peer P50 SoD coverage is 82%.",
    priority: "P1",
    timeline: "Next 90 days",
    roi_band: "Reduced fraud + audit exposure",
    confidence: 0.83,
    source_type: "playbook",
    evidence_refs: ["E3"],
    tags: ["controls", "audit"],
  },
  {
    id: "REG-ZATCA",
    title: "Prepare for ZATCA Phase-2 reciprocal review",
    body: "Sequence e-invoicing integration, validate B2B clearance, and document controls ahead of the 2027 window.",
    priority: "P0",
    timeline: "By Q2 2027",
    roi_band: "Regulatory compliance",
    confidence: 0.95,
    source_type: "regulatory",
    evidence_refs: ["R1"],
    tags: ["regulatory", "tax"],
  },
];

export const SAMPLE_LEAD = {
  lead_id: "sample",
  assessment: {
    country: "United Arab Emirates",
    industry: "Manufacturing",
    function: "Finance",
  },
  results: {
    overall_score: 68,
    risk_level: "Amber",
    category_scores: {
      "Cash & Working Capital": 74,
      "Financial Planning": 61,
      "Controls & Compliance": 58,
      "Reporting Cadence": 79,
    },
    top_gaps: [
      { area: "Controls & Compliance", score: 58 },
      { area: "Financial Planning", score: 61 },
      { area: "Cash & Working Capital", score: 74 },
    ],
  },
  report: {
    executive_summary:
      "Al Nahda Manufacturing is operating in the amber band for finance maturity — comfortably ahead of peers on reporting cadence [E4] but trailing the UAE mid-market median on controls and planning [E2]. Two structural gaps compound near-term risk: the monthly close still takes 12 business days versus a 7-day peer benchmark [E1], and there is no formal three-statement forecast beyond the current quarter. Segregation-of-duties coverage in AP sits at 48% against a peer P50 of 82% [E3]. Addressing all three would unlock approximately AED 4.2M of working-capital headroom and materially reduce audit exceptions ahead of the 2027 ZATCA reciprocal review [R1].",
    gap_analysis: [
      {
        area: "Monthly close cycle",
        finding:
          "The 12-day close is materially slower than the 7-day peer median [E1]. Manual intercompany reconciliation across three subsidiaries is the primary bottleneck.",
        impact:
          "Delays board reporting by 5 days and constrains cash-deployment decisions each cycle.",
      },
      {
        area: "Forecast horizon",
        finding:
          "Forecasting is single-quarter only [E2]. Peer P50 is four quarters, which board-level stakeholders now expect.",
        impact:
          "Limits capital-allocation planning and reduces board confidence in scenario responses.",
      },
      {
        area: "AP controls coverage",
        finding:
          "AP segregation-of-duties coverage is 48% [E3], well below peer P50 of 82%.",
        impact:
          "Elevated fraud exposure and audit-finding risk during ZATCA-adjacent reviews.",
      },
      {
        area: "Reporting cadence",
        finding:
          "Monthly management reporting is on-cadence and above peer P75 for maturity [E4].",
        impact:
          "Strong foundation — extend the same discipline into forecast and controls.",
      },
    ],
    key_risks: [
      "Slow close blocks weekly cash deployment — approximately AED 4M sits idle across the cycle [E1].",
      "Single-quarter forecast horizon limits response speed to demand shocks [E2].",
      "Segregation-of-duties gap in AP creates elevated fraud and audit-finding exposure [E3].",
      "2027 ZATCA reciprocal-review readiness must be sequenced now [R1].",
    ],
    roadmap: {
      next_30_days: [
        {
          title: "Automate intercompany reconciliation",
          detail:
            "Deploy off-the-shelf reconciliation tooling across the three subsidiaries. Target 7-day close by month 2.",
        },
        {
          title: "Two-person AP approval above AED 25k",
          detail:
            "Enforce dual approval on invoices above the AED 25k threshold. Sample below-threshold invoices weekly.",
        },
      ],
      next_90_days: [
        {
          title: "Stand up rolling 4-quarter forecast",
          detail:
            "Move from single-quarter to a rolling 4-quarter cash + P&L forecast anchored on top 3 revenue drivers.",
        },
        {
          title: "Uplift AP SoD coverage to 80%",
          detail:
            "Rework role-based access controls in the ERP; document exceptions in a formal register.",
        },
      ],
      next_180_days: [
        {
          title: "ZATCA Phase-2 integration",
          detail:
            "Sequence e-invoicing integration, validate B2B clearance, document controls ahead of 2027 review.",
        },
        {
          title: "Board-level scenario planning cadence",
          detail:
            "Quarterly scenario reviews with the board using the new rolling forecast.",
        },
      ],
    },
  },
  consultant: {
    name: "Sample Consultant",
    company: "Your Practice",
    biography:
      "In a live report, your bio, photo and firm details appear here — so business leaders see exactly who is reviewing their diagnostic.",
    email: "hello@example.com",
    booking_link: "",
  },
  firmographics: {
    prospect_company_size_band: "Mid-market (100-1000 employees)",
  },
  consultant_perspective:
    "This is where your written perspective appears on real reports. Two or three sentences framing the top gap in your voice go a long way with senior leaders. Business owners consistently tell us the consultant note is the single most persuasive section of the report.",
  whatsapp_link: "",
  mailto_link: "",
  booking_link: "",
  grounding: {
    report_id: "sample",
    pipeline_version: "13.11",
    prompt_fingerprint: "sample-fingerprint",
    fallback_used: false,
    citations: CITATIONS,
    recommendation_cards: RECOMMENDATION_CARDS,
    regulatory_obligations: REGULATORY_OBLIGATIONS,
    execution_metadata: {
      execution_time_ms: 4200,
      signals_used: 4,
      recommendations_used: 4,
      regulatory_rules_triggered: 2,
      validator_drops: 0,
      cache_hit: false,
    },
    confidence_summary: {
      mean_confidence: 0.89,
      citations_count: 4,
      recommendations_count: 3,
      regulatory_rules_count: 2,
      signals_used: 4,
      validator_drops: 0,
      fallback_used: false,
      low_confidence: false,
      stage_errors: [],
    },
  },
};
